import { getActivityThresholdsValues } from 'utils';
import useUser from 'utils/useUser';
import classNames from 'classnames';
import { ReadingType } from 'infrastructure/enums';
import { useEffect, useState, type FC } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getPatientAssignQueryKey } from 'infrastructure/hooks/rpm/assign-tool/use-get-patient-assing';
import { useAssignPatient } from 'infrastructure/hooks/rpm/assign-tool/use-set-assign-patient';
import { useCountdownTimer } from 'infrastructure/hooks/use-count-down-timer';

import {
  getReadingInfo,
  getWeightRiskContent,
  hiddenThresholds,
} from '../../utils/get-reading-info';
import s from './styles.module.scss';
import AssignSwitch from '../assign-switch';

import type {
  StatusKeys,
  TStatuses,
  TThresholdName,
  Value,
} from 'infrastructure/interfaces/i-rpm-patient-activity';
import type { ReadingStatus } from 'infrastructure/enums/reading-statuses';
import type {
  IPatientThresholds,
  IPatientAssignStateChange,
} from 'infrastructure/interfaces';
import type { Nullable } from 'infrastructure/types';

interface PatientReadingColumnProps {
  globalStatus: ReadingStatus;
  values: Value;
  statuses: TStatuses;
  thresholds: IPatientThresholds;
  readingType: ReadingType;
  thresholdName?: Nullable<TThresholdName>;
  patientGuid: string;
  isShowAssignedToggle?: boolean;
}

const PatientReadingColumn: FC<PatientReadingColumnProps> = ({
  globalStatus,
  statuses,
  thresholds,
  values,
  readingType,
  thresholdName,
  patientGuid,
  isShowAssignedToggle,
}) => {
  const queryClient = useQueryClient();
  const assignQueryKey = getPatientAssignQueryKey(patientGuid);
  const { assignee } =
    queryClient.getQueryData<IPatientAssignStateChange>(assignQueryKey) ?? {};

  const [checked, setChecked] = useState(!!assignee);
  const riskItems = getActivityThresholdsValues(
    globalStatus,
    values,
    statuses,
    thresholds,
    readingType,
  );
  const {
    isPatient,
    isMobileDevice,
    loginGuid,
    isAgencyDoctor,
    isCriticalOrAtRiskAlertingRole,
  } = useUser();
  const { assignPatient, isLoading } = useAssignPatient();

  const riskItemsGap = riskItems.length === 3 ? 8 : 12;

  const weightRiskContent = () => {
    const text = getWeightRiskContent(thresholds, thresholdName);
    if (!text) return null;
    const [risk, value] = text.split('≥ ');

    return (
      <p>
        <span>{risk}</span>{' '}
        <span>
          <b>≥ {value}</b>
        </span>
      </p>
    );
  };

  useEffect(() => {
    setChecked(!!assignee);
  }, [!!assignee]);

  const showAssign =
    !isMobileDevice &&
    isShowAssignedToggle &&
    isAgencyDoctor &&
    !isCriticalOrAtRiskAlertingRole;

  const endTime = assignee?.expireAtTimestamp
    ? new Date(assignee?.expireAtTimestamp)
    : undefined;

  useCountdownTimer({
    endTime,
    onFinish: () => {
      if (assignee) {
        queryClient.setQueryData<IPatientAssignStateChange>(
          assignQueryKey,
          (oldData) => {
            if (!oldData) return;

            const newData = structuredClone(oldData);
            newData.assignee = undefined;

            return newData;
          },
        );
      }
    },
  });

  return (
    <div className={classNames(s.reading, { [s['is-patient']]: isPatient })}>
      <div
        data-cy="reading-values"
        className={classNames(s['reading-items'], {
          [s['is-patient']]: isPatient && !isMobileDevice,
        })}
      >
        {Object.keys(values).map((threshold, index, array) => {
          if (hiddenThresholds.includes(threshold)) {
            return null;
          }
          const fontSize = 15;
          const flexDirection =
            array.filter((val) => !hiddenThresholds.includes(val)).length ===
              1 &&
            !isPatient &&
            !isMobileDevice
              ? 'column'
              : 'row';
          const { className, key, value, unit } = getReadingInfo(
            statuses,
            values,
            threshold as StatusKeys,
          );

          return (
            <p
              className={s[className]}
              key={readingType + index}
              style={{ fontSize, flexDirection }}
            >
              <span>{key === 'spo2' ? 'SpO2' : key} </span>
              <span>{`${value} ${unit}`}</span>
            </p>
          );
        })}
      </div>
      <div
        data-cy="reading-thresholds"
        className={s['risk-items']}
        style={{ gap: isPatient && !isMobileDevice ? 4 : riskItemsGap }}
      >
        {readingType === ReadingType.Weight
          ? weightRiskContent()
          : riskItems.length > 0 &&
            riskItems.map((riskItem: string) => {
              const fontSize = riskItems.length < 3 ? 15 : 14;
              const [risk, value] = riskItem?.split(':');

              return (
                <p
                  style={{
                    fontSize: isPatient && !isMobileDevice ? 15 : fontSize,
                    flexDirection:
                      isPatient && !isMobileDevice ? 'row' : 'column',
                  }}
                  key={riskItem}
                >
                  <span>{risk}:</span>
                  <span>{value}</span>
                </p>
              );
            })}
        {showAssign && (
          <AssignSwitch
            checked={checked}
            isLoading={isLoading}
            loginGuid={loginGuid}
            assignee={assignee}
            onChange={() => {
              assignPatient(
                {
                  patientGuid,
                  action: checked ? 'unassign' : 'assign',
                },
                {
                  onSuccess() {
                    setChecked((prev) => !prev);
                  },
                },
              );
            }}
          />
        )}
      </div>
    </div>
  );
};
export default PatientReadingColumn;
